import { useEffect, useState } from 'react';
import Calendar from '../components/Booking/Calendar/Calendar';
import Auth from '../components/Layout/Auth';
import { bookingService, branchService } from '@deliveries-app/services';
import { useSelector, useDispatch } from 'react-redux';
import { addBranches, setLoading } from '../store/actionCreators';
import { useRouter } from 'next/router';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import Loading from '../components/Common/Loading';
import { RootReducerState } from '../store/reducer';
import { getTodos } from '../store/Todos/actions';
import {
  getBookingsTimeStatus,
  handleAsyncBookingEvent,
  rescheduleBooking,
} from '../store/Bookings/actions';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { Booking, Schedule } from '@deliveries-app/models';
import BookingAsyncRefresh from '../components/Booking/AsyncRefresh';

const Index = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const dateBooking = useSelector(
    (state: RootReducerState) => state.dashboard.dateBooking
  );
  const branch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );
  const bookings = useSelector(
    (state: RootReducerState) => state.bookings.bookings
  );
  const loading = useSelector(
    (state: RootReducerState) => state.dashboard.loading
  );

  const selectedBranch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );

  const [schedule, setSchedule] = useState<Schedule>();
  const [date, setDate] = useState<Date>(
    dateBooking ? new Date(dateBooking) : new Date()
  );
  const [create, setCreate] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [description, setDescription] = useState<string>('');
  const [previousDate, setPreviousDate] = useState(date); // Initialise previousDate state

  const { lat, lng, bookingId, duplicate } = router.query;

  const fetchBookings = async (date = null) => {
    if (date) {
      const newDate = moment(date).format('yyyy-MM-DD');

      dispatch(
        getBookingsTimeStatus(branch.id, {
          startDate: newDate,
          endDate: newDate,
        })
      );
    } else {
      getBookingsTimeStatus(branch.id);
    }
  };

  const updateBooking = async (
    branch_id: number,
    booking_id: number,
    vehicle_id: number,
    start_time: string
  ) => {
    dispatch(
      rescheduleBooking(branch_id, booking_id, {
        start_time: moment(date).format('MM/DD/YYYY') + ' ' + start_time,
        vehicle_id,
      })
    );
  };

  /**
   * Fetch daily schedule for the current branch
   */
  const fetchSchedule = async (day?: string) => {
    if (day) {
      day = moment(day).tz(branch.timezone).format('YYYY-MM-DD');
    }

    try {
      const schedules = await branchService.getSchedule(
        branch.id,
        day ?? moment(date).tz(branch.timezone).format('YYYY-MM-DD')
      );

      setSchedule(schedules[0]);
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateSchedule = async (schedule: Schedule) => {
    try {
      console.log('Schedule to update: ', schedule);

      const updatedSchedule = await branchService.updateSchedule(
        branch.id,
        schedule
      );

      setSchedule(updatedSchedule);
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const findDuration = async (start_time: Date) => {
    if (!lat || !lng) return;

    console.log(
      'Fetching estimated time to create a new booking for lat,lng:',
      {
        lat,
        lng,
      }
    );
    try {
      const result = await bookingService.findEstimatedBookingTime(
        branch.id,
        lat as string,
        lng as string,
        start_time
      );
      setDuration(result.duration);
      if (result.warning) {
        toast(result.warning, {
          icon: 'ℹ️',
          duration: 6000,
        });
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await branchService.getBranches();

      dispatch(addBranches(res[0]));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (date !== previousDate) {
      setPreviousDate(date);
      return;
    }

    const interval = setInterval(() => {
      if (selectedBranch == null) {
        fetchBranches();
      } else {
        dispatch(addBranches(selectedBranch));
      }
    }, 20 * 1000);

    return () => clearInterval(interval);
  }, [date]);

  useEffect(() => {
    if (!branch) return;
    fetchSchedule();
    findDuration(new Date());
    fetchBookings(date);

    if (lat && lng) setCreate(true);
  }, [branch, date]);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (
      urlParams.get('success') == 'true' ||
      urlParams.get('reschedule') == 'false'
    ) {
      setDate(new Date(dateBooking));
    } else {
      setDate(new Date());
    }
  }, []);

  return (
    <Auth>
      <BookingAsyncRefresh />

      {loading && bookings.length === 0 && (
        <div className="fixed left-24 top-0 z-[20] flex h-full w-full items-center justify-center bg-white  text-gray-800">
          <div className="flex animate-pulse flex-col items-center">
            <CalendarDaysIcon
              className={`h-24 w-24`}
              style={{
                color: branch.branch_colour,
              }}
            />
            Building your dashboard...
          </div>
        </div>
      )}

      {bookings && branch && (
        <Calendar
          bookings={bookings}
          updateBooking={updateBooking}
          updateSchedule={updateSchedule}
          fetchSchedule={fetchSchedule}
          schedule={schedule}
          date={date}
          setDate={setDate}
          create={create}
          duration={duration}
          description={description}
          fetchBookings={fetchBookings}
          bookingId={bookingId === 'undefined' ? '' : bookingId}
          duplicate={duplicate}
        />
      )}
    </Auth>
  );
};

export default Index;
